import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { FloorSvg } from '@components/FloorSvg/FloorSvg';
import { ScrollArea } from '@components/ui/ScrollArea/ScrollArea';
import { Tooltip } from '@components/ui/Tooltip/Tooltip';
import { getEquipmentIconFromType } from '@utils/equipmentIcons';
import { nameInitials } from '@utils/nameInitials';
import { professionChipStyle } from '@utils/professionColor';
import { tradeLabel } from '@utils/tradeLabel';
import {
  markerAreaId,
  readSvgAnchorCentres,
  svgElementScreenRect,
  tightenSvgViewBox,
  type ISvgAnchor,
} from '@utils/svgMarkers';
import {
  aggregateUtilitiesByType,
  type EquipmentPresence,
  type ManagerPresence,
  type UtilityPresence,
} from '@store/selectors/monitor.selectors';
import { CloseIcon } from './floorDrawerIcons';
import styles from './floorDrawer.module.css';

export type TradePresence = {
  professionId: string;
  /** Specific trade — used to look up the localized chip label via
   *  `tradeLabel(t, tradeId)` against the i18next `trades.*` namespace. */
  tradeId: string;
  /** Broad category fallback when no translation exists. */
  tradeGroup: string | undefined;
  color: string;
  count: number;
};

// ---------- Floor SVG view (shared between drawer body + fullscreen modal) ----------

interface IFloorPlanViewProps {
  siteId: string;
  floorId: string;
  personnelByAreaId: Map<string, TradePresence[]>;
  /** equipment presences per area, keyed by lowercased areaId */
  equipmentByAreaId: Map<string, EquipmentPresence[]>;
  /** utility presences per area, keyed by lowercased areaId */
  utilitiesByAreaId: Map<string, UtilityPresence[]>;
  /** manager presences per area, keyed by lowercased areaId */
  managersByAreaId: Map<string, ManagerPresence[]>;
}

/**
 * Renders the floor SVG (via the shared `<FloorSvg>` primitive) with the
 * per-trade worker chip overlay. Two instances coexist when the modal is
 * open — both reference the same area ids so the global stylesheet rules
 * from the drawer's CSS-injection effect apply to both. Both `<FloorSvg>`
 * instances share their underlying `useQuery` cache via queryKey, so the
 * SVG is fetched once and reused.
 */
export function FloorPlanView({
  siteId,
  floorId,
  personnelByAreaId,
  equipmentByAreaId,
  utilitiesByAreaId,
  managersByAreaId,
}: IFloorPlanViewProps) {
  const { t } = useTranslation();
  const containerRef = useRef<HTMLDivElement | null>(null);
  // The `.svgStack` root — wraps both `<FloorSvg>` (the SVG) and the sibling
  // `.workersOverlay` (the chip stacks). The scale effect queries from here so
  // it can see the stacks AND the SVG's area outlines in one subtree.
  const svgStackRef = useRef<HTMLDivElement | null>(null);
  const [anchors, setAnchors] = useState<ISvgAnchor[]>([]);

  // Tighten the SVG viewBox to its content bbox so artboard whitespace
  // doesn't push the floor plan off-centre. Same trick as BuildingViewSvg.
  // Re-runs on (siteId, floorId) change since `<FloorSvg>` re-injects the
  // SVG on a fetch result for the new key.
  useEffect(() => {
    const svg = containerRef.current?.querySelector<SVGSVGElement>('svg');
    if (svg) tightenSvgViewBox(svg);
  }, [siteId, floorId]);

  // Measure workers anchor centres post-mount + on resize. Each instance
  // (drawer + modal) measures its own copy of the SVG, since the rendered
  // sizes differ.
  useEffect(() => {
    const root = containerRef.current;
    if (!root) return;
    const measure = () => setAnchors(readSvgAnchorCentres(root, 'workers'));
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(root);
    return () => ro.disconnect();
  }, [siteId, floorId]);

  // Stable structural fingerprint of the per-area chips — which areas have
  // chips and how many each holds. Drives the scale effect so it only re-runs
  // (and forces layout) when the chip layout actually changes, not on every
  // RTLS tick: the presence maps get new identities each tick even when nothing
  // visible changed, so depending on them directly would thrash layout.
  const chipLayoutKey = useMemo(() => {
    const parts: string[] = [];
    for (const [id, presences] of personnelByAreaId) parts.push(`p:${id}:${presences.length}`);
    for (const [id, presences] of equipmentByAreaId) parts.push(`e:${id}:${presences.length}`);
    for (const [id, presences] of utilitiesByAreaId) parts.push(`u:${id}:${presences.length}`);
    for (const [id, presences] of managersByAreaId) parts.push(`m:${id}:${presences.length}`);
    return parts.join('|');
  }, [personnelByAreaId, equipmentByAreaId, utilitiesByAreaId, managersByAreaId]);

  // Scale each crowded chip stack down so it stays inside its area outline —
  // the floor-plan analogue of the building view's `chipScale`. We read the
  // area's `area<id>` outline bbox (in screen px) and shrink the stack until it
  // fits, with a margin so chips don't touch the walls and a floor so the count
  // stays legible. `offsetWidth/Height` give the stack's wrapped *layout* size
  // (unaffected by the scale transform), so the measurement is stable across
  // runs. useLayoutEffect runs after layout (the wrapped size is final) and
  // before paint (no flash). Each instance (drawer + modal) scales its own copy.
  useLayoutEffect(() => {
    // Query from `.svgStack` (not `containerRef`/`<FloorSvg>`): the chip stacks
    // live in the sibling `.workersOverlay`, while the area outlines live in the
    // SVG — both are under `.svgStack`.
    const root = svgStackRef.current;
    if (!root) return;
    // Resolve every area outline once (a single subtree query) into a lookup,
    // instead of a per-stack case-insensitive attribute selector in the loop.
    const areaOutlines = new Map<string, SVGGraphicsElement>();
    root.querySelectorAll<SVGGraphicsElement>('[id*="area"]').forEach((el) => {
      const id = markerAreaId(el.id, 'area');
      // First match wins (matches the old per-area querySelector) — duplicate
      // `_<n>` ids from Illustrator resolve to the same area.
      if (id && !areaOutlines.has(id.toLowerCase())) areaOutlines.set(id.toLowerCase(), el);
    });
    // Chips occupy ≤ FILL_RATIO of the area (leaving a visible gap); never
    // shrink past MIN_SCALE so the count stays legible — a very crowded area
    // then slightly exceeds its outline rather than rendering unreadably tiny.
    const FILL_RATIO = 0.9;
    const MIN_SCALE = 0.6;
    root.querySelectorAll<HTMLElement>('[data-area-id]').forEach((stack) => {
      const areaId = stack.dataset.areaId;
      const stackWidth = stack.offsetWidth;
      const stackHeight = stack.offsetHeight;
      const areaOutline = areaId ? areaOutlines.get(areaId.toLowerCase()) : undefined;
      let scale = 1;
      if (areaOutline && stackWidth > 0 && stackHeight > 0) {
        const areaRect = svgElementScreenRect(areaOutline);
        if (areaRect && areaRect.width > 0 && areaRect.height > 0) {
          scale = Math.max(
            MIN_SCALE,
            Math.min(
              1,
              (areaRect.width * FILL_RATIO) / stackWidth,
              (areaRect.height * FILL_RATIO) / stackHeight,
            ),
          );
        }
      }
      stack.style.setProperty('--chip-scale', String(Math.round(scale * 1000) / 1000));
    });
  }, [anchors, chipLayoutKey]);

  return (
    <div className={styles.svgStack} ref={svgStackRef}>
      <FloorSvg
        ref={containerRef}
        siteId={siteId}
        floorId={floorId}
        className={styles.svgWrap}
      />
      <div className={styles.workersOverlay} aria-hidden="true">
        {anchors.map((a) => {
          const key = a.areaId.toLowerCase();
          const trades = personnelByAreaId.get(key) ?? [];
          const equipment = equipmentByAreaId.get(key) ?? [];
          const utilities = utilitiesByAreaId.get(key) ?? [];
          const managers = managersByAreaId.get(key) ?? [];
          if (
            trades.length === 0 &&
            equipment.length === 0 &&
            utilities.length === 0 &&
            managers.length === 0
          )
            return null;
          // One chip per utility type so hoists and scissor lifts get
          // their own icon + count.
          const utilityChips = aggregateUtilitiesByType(utilities);
          return (
            <div
              key={a.areaId}
              className={styles.workersChipStack}
              style={{ left: a.x, top: a.y }}
              data-area-id={a.areaId}
            >
              {/* Manager pin: just the initials avatar circle to keep the
                  plan uncluttered; the full name shows on hover via the
                  styled, instant Trusstor tooltip (replacing native `title`). */}
              {managers.map((mgr) => (
                <Tooltip key={mgr.id} content={mgr.name} delay={0}>
                  <span
                    className={styles.managerAvatar}
                    style={professionChipStyle(mgr.backgroundColor)}
                  >
                    {nameInitials(mgr.name)}
                  </span>
                </Tooltip>
              ))}
              {trades.map((trade) => (
                <Tooltip
                  key={trade.professionId}
                  content={`${tradeLabel(t, trade.tradeId) || trade.tradeGroup || ''}: ${trade.count}`}
                  delay={0}
                >
                  <span
                    className={styles.workersChip}
                    style={professionChipStyle(trade.color)}
                  >
                    {trade.count}
                  </span>
                </Tooltip>
              ))}
              {/* One chip per equipment type (icon + count). */}
              {equipment.map((eq) => (
                <Tooltip key={eq.type} content={`${eq.type}: ${eq.count}`} delay={0}>
                  <span className={styles.equipmentChip}>
                    <img
                      className={styles.equipmentChipIcon}
                      src={getEquipmentIconFromType(eq.type)}
                      alt=""
                    />
                    <span className={styles.equipmentChipCount}>{eq.count}</span>
                  </span>
                </Tooltip>
              ))}
              {utilityChips.map((u) => (
                <Tooltip key={u.type} content={`${u.type}: ${u.count}`} delay={0}>
                  <span className={styles.utilityChip}>
                    {u.hasLive && (
                      <span className={styles.liveDot} aria-hidden="true" />
                    )}
                    <img
                      className={styles.equipmentChipIcon}
                      src={getEquipmentIconFromType(u.type)}
                      alt=""
                    />
                    <span className={styles.equipmentChipCount}>{u.count}</span>
                  </span>
                </Tooltip>
              ))}
            </div>
          );
        })}
      </div>
    </div>
  );
}

interface IFloorPlanModalProps {
  siteId: string;
  floorId: string;
  personnelByAreaId: Map<string, TradePresence[]>;
  equipmentByAreaId: Map<string, EquipmentPresence[]>;
  utilitiesByAreaId: Map<string, UtilityPresence[]>;
  managersByAreaId: Map<string, ManagerPresence[]>;
  title: string;
  onClose: () => void;
}

/**
 * Fullscreen modal showing the floor plan in a centred card. Mounts via
 * portal to `document.body` so it escapes the drawer's z-stack and overlays
 * the entire page (mirrors trusstor_dashboard's modal-on-expand behaviour).
 */
export function FloorPlanModal({
  siteId,
  floorId,
  personnelByAreaId,
  equipmentByAreaId,
  utilitiesByAreaId,
  managersByAreaId,
  title,
  onClose,
}: IFloorPlanModalProps) {
  const { t } = useTranslation();
  return createPortal(
    // Only a click on the backdrop itself closes; clicks inside the card
    // (the SVG, the scrollbar) bubble here with a different target.
    <div
      className={styles.modalBackdrop}
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
      role="presentation"
    >
      <ScrollArea
        className={styles.modalCard}
        viewportClassName={styles.modalCardViewport}
        viewportProps={{ role: 'dialog', 'aria-label': title }}
      >
        <button
          type="button"
          className={styles.modalClose}
          onClick={onClose}
          aria-label={t('monitor.floorDrawer.modalClose')}
        >
          <CloseIcon />
        </button>
        <FloorPlanView
          siteId={siteId}
          floorId={floorId}
          personnelByAreaId={personnelByAreaId}
          equipmentByAreaId={equipmentByAreaId}
          utilitiesByAreaId={utilitiesByAreaId}
          managersByAreaId={managersByAreaId}
        />
      </ScrollArea>
    </div>,
    document.body,
  );
}
